import React from 'react';
import { Box, Paper, Typography, Stack, TextField, InputAdornment, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Button, Pagination, Chip, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { listAuditLogs, exportAuditLogsCsv } from '../api/auditLogs';
import type { AuditLogDto } from '../api/auditLogs';
import { useTranslation } from 'react-i18next';

const AuditLogsPage: React.FC = () => {
  const { t } = useTranslation();
  const [q, setQ] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [userId, setUserId] = React.useState('');
  const [action, setAction] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(20);
  const [total, setTotal] = React.useState(0);
  const [rows, setRows] = React.useState<AuditLogDto[]>([]);

  const load = React.useCallback(async () => {
    const res = await listAuditLogs({ page, limit, q: q || undefined, from: from || undefined, to: to || undefined, userId: userId || undefined, action: action || undefined } as any);
    setRows(res.data);
    setTotal(res.total);
  }, [page, limit, q]);
  // incluir deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load, from, to, userId, action]);

  const handleExport = async () => {
    const blob = await exportAuditLogsCsv();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-logs.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('audit.title', 'Auditoría')}</Typography>
        <Stack direction="row" spacing={2}>
          <TextField size="small" placeholder={t('common.search', 'Buscar...')} value={q} onChange={e => { setPage(1); setQ(e.target.value); }} InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon /></InputAdornment>
          ) }} />
          <TextField size="small" type="date" label={t('common.from', 'Desde')} InputLabelProps={{ shrink: true }} value={from} onChange={e => { setPage(1); setFrom(e.target.value); }} />
          <TextField size="small" type="date" label={t('common.to', 'Hasta')} InputLabelProps={{ shrink: true }} value={to} onChange={e => { setPage(1); setTo(e.target.value); }} />
          <TextField size="small" label={t('audit.userId', 'User ID')} value={userId} onChange={e => { setPage(1); setUserId(e.target.value); }} />
          <TextField size="small" select label={t('audit.action', 'Acción')} value={action} onChange={e => { setPage(1); setAction(e.target.value); }} sx={{ minWidth: 160 }}>
            <MenuItem value="">{t('common.all', 'Todas')}</MenuItem>
            <MenuItem value="auth.login">auth.login</MenuItem>
            <MenuItem value="auth.login_failed">auth.login_failed</MenuItem>
            <MenuItem value="user.create">user.create</MenuItem>
            <MenuItem value="user.update">user.update</MenuItem>
            <MenuItem value="user.delete">user.delete</MenuItem>
            <MenuItem value="user.read">user.read</MenuItem>
            <MenuItem value="user.list">user.list</MenuItem>
          </TextField>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>{t('audit.exportCsv', 'Exportar CSV')}</Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('audit.action', 'Acción')}</TableCell>
              <TableCell>{t('audit.resource', 'Recurso')}</TableCell>
              <TableCell>{t('audit.description', 'Descripción')}</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>User Agent</TableCell>
              <TableCell>{t('audit.userId', 'User ID')}</TableCell>
              <TableCell>{t('common.date', 'Fecha')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.id}</TableCell>
                <TableCell><Chip size="small" label={r.action} /></TableCell>
                <TableCell>{r.resource}</TableCell>
                <TableCell>{r.description}</TableCell>
                <TableCell>{r.ipAddress}</TableCell>
                <TableCell>{r.userAgent?.slice(0, 40)}</TableCell>
                <TableCell>{r.userId}</TableCell>
                <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">{t('common.empty', 'Sin registros')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination page={page} onChange={(_, p) => setPage(p)} count={Math.max(1, Math.ceil(total / limit))} color="primary" />
      </Stack>
    </Box>
  );
};

export default AuditLogsPage;


